import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  LinearProgress
} from '@mui/material';
import {
  Payment as PayrollIcon,
  Add as AddIcon,
  PlayArrow as ProcessIcon,
  Receipt as PayslipIcon,
  Assessment as ReportsIcon,
  CheckCircle as CompleteIcon,
  Schedule as PendingIcon,
  Error as ErrorIcon,
  Download as DownloadIcon,
  Send as SendIcon,
  Flag as USFlagIcon
} from '@mui/icons-material';

interface PayrollRecord {
  id: number;
  employee_id: number;
  employee_name: string;
  employee_number: string;
  department: string;
  pay_period_start: string;
  pay_period_end: string;
  pay_date: string;
  basic_pay: number;
  overtime_pay: number;
  bonus: number;
  gross_pay: number;
  income_tax: number;
  social_security_employee: number;
  social_security_employer: number;
  pension_employee: number;
  pension_employer: number;
  student_loan_deduction: number;
  total_deductions: number;
  net_pay: number;
  status: 'draft' | 'processed' | 'paid' | 'error';
  tax_code: string;
}

const PayrollManagement: React.FC = () => {
  const [payrollRecords, setPayrollRecords] = useState<PayrollRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [processDialogOpen, setProcessDialogOpen] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState({
    start: new Date().toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0],
    pay_date: new Date().toISOString().split('T')[0]
  });
  const [processingStep, setProcessingStep] = useState(0);
  const [usRates, setUsRates] = useState<any>(null);

  useEffect(() => {
    fetchPayrollRecords();
    fetchUSRates();
  }, []);

  const fetchPayrollRecords = async () => {
    // Mock data for demonstration
    setPayrollRecords([
      {
        id: 1,
        employee_id: 1,
        employee_name: 'Sarah Johnson',
        employee_number: 'EMP001',
        department: 'Sales',
        pay_period_start: '2024-01-01',
        pay_period_end: '2024-01-31',
        pay_date: '2024-01-31',
        basic_pay: 3750.00,
        overtime_pay: 0.00,
        bonus: 0.00,
        gross_pay: 3750.00,
        income_tax: 450.00,
        social_security_employee: 300.00,
        social_security_employer: 387.50,
        pension_employee: 112.50,
        pension_employer: 112.50,
        student_loan_deduction: 0.00,
        total_deductions: 862.50,
        net_pay: 2887.50,
        status: 'paid',
        tax_code: 'W-4 Standard'
      },
      {
        id: 2,
        employee_id: 2,
        employee_name: 'James Wilson',
        employee_number: 'EMP002',
        department: 'Warehouse',
        pay_period_start: '2024-01-01',
        pay_period_end: '2024-01-31',
        pay_date: '2024-01-31',
        basic_pay: 2000.00,
        overtime_pay: 300.00,
        bonus: 0.00,
        gross_pay: 2300.00,
        income_tax: 146.00,
        social_security_employee: 156.00,
        social_security_employer: 237.40,
        pension_employee: 69.00,
        pension_employer: 69.00,
        student_loan_deduction: 0.00,
        total_deductions: 371.00,
        net_pay: 1929.00,
        status: 'processed',
        tax_code: 'W-4 Standard'
      }
    ]);
  };

  const fetchUSRates = async () => {
    // Mock US federal tax rates data
    setUsRates({
      tax_year: '2024',
      standard_deduction: 14600.00,
      bracket_threshold: 47150.00,
      tax_rates: {
        rate_10: 0.10,
        rate_22: 0.22,
        rate_24: 0.24
      },
      social_security: {
        employee_rate: 0.0765,
        employer_rate: 0.0765
      }
    });
  };

  const handleProcessPayroll = async () => {
    setLoading(true);
    setProcessingStep(0);
    
    // Simulate payroll processing steps
    const steps = [
      'Validating employee data...',
      'Calculating gross pay...',
      'Computing US tax deductions...',
      'Processing Social Security...',
      'Calculating pension contributions...',
      'Generating payslips...',
      'Finalizing payroll...'
    ];

    for (let i = 0; i < steps.length; i++) {
      setProcessingStep(i);
      await new Promise(resolve => setTimeout(resolve, 1000));
    }

    setProcessingStep(steps.length);
    setLoading(false);
    setProcessDialogOpen(false);
    
    // Refresh payroll records
    fetchPayrollRecords();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'success';
      case 'processed': return 'info';
      case 'draft': return 'warning';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid': return <CompleteIcon />;
      case 'processed': return <PendingIcon />;
      case 'draft': return <PendingIcon />;
      case 'error': return <ErrorIcon />;
      default: return <PendingIcon />;
    }
  };

  const totalGrossPay = payrollRecords.reduce((sum, record) => sum + record.gross_pay, 0);
  const totalNetPay = payrollRecords.reduce((sum, record) => sum + record.net_pay, 0);
  const totalTax = payrollRecords.reduce((sum, record) => sum + record.income_tax, 0);
  const totalNI = payrollRecords.reduce((sum, record) => sum + record.social_security_employee + record.social_security_employer, 0);

  return (
    <Box sx={{ p: 3, pt: 2 }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 4,
        mt: 2
      }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Payroll Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            US-compliant payroll processing with IRS integration
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<ProcessIcon />}
          onClick={() => setProcessDialogOpen(true)}
          size="large"
          sx={{ minWidth: 180 }}
        >
          Process Payroll
        </Button>
      </Box>

      {/* US Compliance Alert */}
      <Alert severity="success" sx={{ mb: 4 }}>
        <Typography variant="subtitle2" gutterBottom>
          🇺🇸 IRS Compliant
        </Typography>
        <Typography variant="body2">
          All payroll calculations use current US federal tax rates and are fully compliant with IRS requirements. 
          Automatic W-2 and Form 941 generation included.
        </Typography>
      </Alert>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <PayrollIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" color="primary">
                {formatCurrency(totalGrossPay)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Gross Pay
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <CompleteIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" color="success.main">
                {formatCurrency(totalNetPay)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Net Pay
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <USFlagIcon color="warning" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" color="warning.main">
                {formatCurrency(totalTax)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Federal Tax
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <PayrollIcon color="info" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" color="info.main">
                {formatCurrency(totalNI)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Social Security
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* US Tax Rates Info */}
      {usRates && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <USFlagIcon color="primary" />
              Current US Federal Tax Rates ({usRates.tax_year})
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <Typography variant="subtitle2">Standard Deduction</Typography>
                <Typography variant="h6" color="success.main">
                  {formatCurrency(usRates.standard_deduction)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="subtitle2">22% Bracket</Typography>
                <Typography variant="h6" color="info.main">
                  Up to {formatCurrency(usRates.bracket_threshold)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="subtitle2">Employee FICA (7.65%)</Typography>
                <Typography variant="h6" color="warning.main">
                  On all wages
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="subtitle2">Employer FICA (7.65%)</Typography>
                <Typography variant="h6" color="error.main">
                  On all wages
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Payroll Records Table */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Recent Payroll Records</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button startIcon={<PayslipIcon />} variant="outlined" size="small">
                Generate Payslips
              </Button>
              <Button startIcon={<DownloadIcon />} variant="outlined" size="small">
                Export to Excel
              </Button>
              <Button startIcon={<SendIcon />} variant="outlined" size="small">
                Submit to IRS
              </Button>
            </Box>
          </Box>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Pay Period</TableCell>
                  <TableCell align="right">Gross Pay</TableCell>
                  <TableCell align="right">Tax</TableCell>
                  <TableCell align="right">FICA</TableCell>
                  <TableCell align="right">Net Pay</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payrollRecords.map((record) => (
                  <TableRow key={record.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2">
                          {record.employee_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {record.employee_number} • {record.department}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(record.pay_period_start)} - {formatDate(record.pay_period_end)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Pay Date: {formatDate(record.pay_date)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="subtitle2">
                        {formatCurrency(record.gross_pay)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {formatCurrency(record.income_tax)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {formatCurrency(record.social_security_employee + record.social_security_employer)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="subtitle2" color="success.main">
                        {formatCurrency(record.net_pay)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={record.status.charAt(0).toUpperCase() + record.status.slice(1)}
                        color={getStatusColor(record.status) as any}
                        size="small"
                        icon={getStatusIcon(record.status)}
                      />
                    </TableCell>
                    <TableCell>
                      <Button size="small" startIcon={<PayslipIcon />}>
                        Payslip
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Process Payroll Dialog */}
      <Dialog open={processDialogOpen} onClose={() => setProcessDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ProcessIcon color="primary" />
            Process Payroll
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              �🇸 US Payroll Processing
            </Typography>
            <Typography variant="body2">
              This will process payroll for all active employees using current US federal tax rates and IRS compliance rules.
            </Typography>
          </Alert>

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Pay Period Start"
                type="date"
                value={selectedPeriod.start}
                onChange={(e) => setSelectedPeriod(prev => ({ ...prev, start: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Pay Period End"
                type="date"
                value={selectedPeriod.end}
                onChange={(e) => setSelectedPeriod(prev => ({ ...prev, end: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Pay Date"
                type="date"
                value={selectedPeriod.pay_date}
                onChange={(e) => setSelectedPeriod(prev => ({ ...prev, pay_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          {loading && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>Processing Payroll...</Typography>
              <LinearProgress sx={{ mb: 2 }} />
              <List dense>
                {[
                  'Validating employee data...',
                  'Calculating gross pay...',
                  'Computing US tax deductions...',
                  'Processing Social Security...',
                  'Calculating pension contributions...',
                  'Generating payslips...',
                  'Finalizing payroll...'
                ].map((step, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      {index < processingStep ? <CompleteIcon color="success" /> : 
                       index === processingStep ? <PendingIcon color="primary" /> : 
                       <PendingIcon color="disabled" />}
                    </ListItemIcon>
                    <ListItemText 
                      primary={step}
                      sx={{ 
                        color: index <= processingStep ? 'text.primary' : 'text.disabled'
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProcessDialogOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleProcessPayroll}
            disabled={loading}
          >
            {loading ? 'Processing...' : 'Process Payroll'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PayrollManagement;
